<template>
  <!-- Confirmation Modal -->
  <ProductConfirmationModal
    v-if="item && isConfirmationShown && item.alertMessage"
    :is-open="isConfirmationShown"
    :message="item.alertMessage"
    @confirm="handleConfirm"
    @cancel="onClose"
  />

  <!-- Main Modal -->
  <Modal
    v-else-if="item && !isConfirmationShown"
    :is-open="!!item && !isConfirmationShown"
    size="xl"
    @close="onClose"
    :close-on-backdrop="true"
    :container-class="modalContainerClass"
    container-style="height: 95vh; max-height: 95vh;"
    :content-class="modalContentClass"
    :scrollable="false"
    close-button-class="absolute top-4 right-4 z-40 rounded-full !min-w-0 !w-10 !h-10 !aspect-square flex items-center justify-center"
  >
    <template v-if="isSingleColumnProduct">
      <div
        ref="rightColumnScroll"
        class="flex-1 overflow-y-auto bg-gray-50"
        @touchstart.passive="onModifierPanelTouchStart"
        @scroll="onRightColumnScroll"
      >
        <ProductInfo
          :product="item"
          :total-price="getTotalPrice()"
          :show-image="true"
          container-class="w-full bg-gray-50 flex flex-col border-b border-gray-100 min-h-0 shrink-0"
        />
        <ProductNotes :value="note" @input="note = $event" class="px-6" />
      </div>

      <!-- Sticky Footer -->
      <div
        class="p-4 border-t border-gray-100 bg-white z-20"
        style="box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);"
      >
        <div class="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <QuantitySelector
            :value="quantity"
            :min="1"
            @input="quantity = $event"
          />
          <Button
            variant="primary"
            size="lg"
            :disabled="!isValid"
            :full-width="true"
            :badge="formatPrice(getTotalPrice())"
            @click="handleSubmit"
            custom-class="flex justify-between items-center"
          >
            {{ submitButtonLabel }}
          </Button>
        </div>
      </div>
    </template>

    <template v-else>
      <!-- LEFT COLUMN: Product info + live selections summary -->
      <div
        class="w-full md:w-5/12 flex flex-col bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 min-h-0 shrink-0 overflow-visible md:overflow-hidden"
      >
        <div class="flex-1 min-h-0 md:overflow-y-auto">
          <ProductInfo
            :product="item"
            :total-price="getTotalPrice()"
            :show-image="showProductImage"
            :collapsible="true"
            container-class="w-full bg-gray-50 flex flex-col"
            @toggle-details="toggleDetails"
          />

          <!-- Selections summary (desktop only) -->
          <div class="hidden md:block px-6 pt-2 pb-6">
            <div class="bg-white border border-gray-200 rounded-xl p-4">
              <div
                class="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2"
              >
                Your selections
              </div>
              <div v-if="selectionSummary.length" class="space-y-1">
                <div
                  v-for="(sel, i) in selectionSummary"
                  :key="`sel-${i}`"
                  class="flex justify-between items-center text-sm text-gray-700"
                >
                  <span class="flex items-center gap-1.5 min-w-0">
                    <Check :size="14" class="text-green-500 flex-none" />
                    <span class="truncate">{{ sel.name }}</span>
                  </span>
                  <span class="text-gray-500 flex-none ml-2">
                    {{ sel.price > 0 ? formatPrice(sel.price) : "" }}
                  </span>
                </div>
              </div>
              <div v-else class="text-sm text-gray-400 italic">
                No selections yet
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Step-by-step customization -->
      <div
        class="w-full md:w-7/12 flex-1 flex flex-col bg-white min-h-0 overflow-hidden"
      >
        <!-- Status line + progress (all sizes) -->
        <div
          v-if="hasModifierGroups && modifierGroups.length > 1"
          class="px-4 md:px-6 pt-4 pr-12 md:pr-16"
        >
          <div
            class="flex justify-between items-center text-sm md:text-base text-gray-500 font-medium mb-2"
          >
            <span>
              Step
              <strong class="text-gray-900">{{ currentGroupIndex + 1 }}</strong>
              of
              <strong class="text-gray-900">{{ modifierGroups.length }}</strong>
            </span>
            <span
              v-if="hasRequiredModifiers"
              :class="
                requiredRemaining === 0 ? 'text-green-600' : 'text-gray-500'
              "
            >
              {{
                requiredRemaining === 0
                  ? "All required selections complete"
                  : `${requiredRemaining} required left`
              }}
            </span>
          </div>
          <div class="flex gap-1 pb-3">
            <div
              v-for="(meta, i) in groupMeta"
              :key="`step-${i}`"
              class="flex-1 h-1 rounded-full transition-all"
              :class="stepBarClass(meta, i)"
            />
          </div>
        </div>

        <!-- Group tabs (top navigation, all sizes) -->
        <div
          v-if="hasModifierGroups && modifierGroups.length > 1"
          class="flex overflow-x-auto border-b border-gray-100 bg-gray-50/60 px-2 md:px-4"
        >
          <button
            v-for="(meta, i) in groupMeta"
            :key="`tab-${i}`"
            type="button"
            @click="gotoGroup(i)"
            class="flex-none flex items-center gap-1.5 px-3 md:px-4 py-2.5 md:py-3 text-xs md:text-sm font-medium whitespace-nowrap border-b-2 transition-all"
            :class="
              i === currentGroupIndex
                ? 'text-primary-600 border-primary-600'
                : 'text-gray-500 border-transparent hover:text-gray-900'
            "
          >
            {{ meta.name }}
            <span
              class="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
              :class="badgeClass(meta.status)"
            >
              {{ meta.tabBadge }}
            </span>
          </button>
        </div>

        <!-- Active group panel -->
        <div
          ref="rightColumnScroll"
          class="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-6 min-h-0"
          @touchstart.passive="onModifierPanelTouchStart"
          @scroll="onRightColumnScroll"
        >
          <ProductModifiers
            :modifier-groups="modifierGroups"
            :current-group-index="currentGroupIndex"
            :selected-modifiers="selectedModifiers"
            :has-attempted-submit="hasAttemptedSubmit"
            :currency="currency"
            :loading="loadingModifiers"
            @modifier-selected="handleModifierSelected"
          />

          <!-- Notes (shown on the last step) -->
          <ProductNotes
            v-if="hasModifierGroups && isLastGroup"
            :value="note"
            @input="note = $event"
          />
        </div>

        <!-- Sticky Footer -->
        <div
          class="p-4 border-t border-gray-100 bg-white z-20"
          style="box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);"
        >
          <div class="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <QuantitySelector
              :value="quantity"
              :min="1"
              @input="quantity = $event"
            />

            <button
              v-if="hasModifierGroups"
              type="button"
              :disabled="ctaState.disabled"
              @click="handleCtaClick"
              class="flex-1 flex items-center justify-between gap-2 px-4 py-3 rounded-xl font-semibold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              :class="ctaColorClass"
            >
              <span class="flex flex-col items-start leading-tight min-w-0">
                <span class="text-base truncate">{{ ctaState.label }}</span>
                <span class="text-xs font-normal opacity-90 mt-0.5 truncate">
                  {{ ctaState.hint }}
                </span>
              </span>
              <span class="flex items-center text-base font-semibold flex-none">
                <component
                  :is="ctaState.icon"
                  v-if="ctaState.icon"
                  :size="20"
                />
                <span v-else>{{ ctaState.right }}</span>
              </span>
            </button>

            <!-- Fallback button while modifiers are still loading -->
            <Button
              v-else
              variant="primary"
              size="lg"
              :disabled="!isValid"
              :full-width="true"
              :badge="formatPrice(getTotalPrice())"
              @click="handleSubmit"
              custom-class="flex justify-between items-center"
            >
              {{ submitButtonLabel }}
            </Button>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapGetters } from "vuex";
import { useItemModal } from "@/composables/useItemModal";
import { getProductModifierGroups } from "@/helpers/apiHelper";
import ProductConfirmationModal from "./ProductConfirmationModal.vue";
import ProductInfo from "./ProductInfo.vue";
import ProductModifiers from "./ProductModifiers.vue";
import ProductNotes from "./ProductNotes.vue";
import QuantitySelector from "./QuantitySelector.vue";
import Button from "@/components/ui/Button.vue";
import Modal from "@/components/ui/Modal.vue";

export default {
  name: "ItemModal",
  components: {
    ProductConfirmationModal,
    ProductInfo,
    ProductModifiers,
    ProductNotes,
    QuantitySelector,
    Button,
    Modal
  },
  props: {
    item: {
      type: Object,
      default: null
    },
    onAddToCart: {
      type: Function,
      required: true
    },
    /** When set, modal is in "edit" mode: show Update and call onUpdateCartItem instead of onAddToCart */
    editingCartId: {
      type: String,
      default: null
    },
    onUpdateCartItem: {
      type: Function,
      default: null
    },
    onClose: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      quantity: 1,
      note: "",
      modifierGroups: [],
      singleSelections: {},
      currentGroupIndex: 0,
      isConfirmationShown: false,
      hasAttemptedSubmit: false,
      loadingModifiers: false,
      currentFetchProductId: null,
      rightColumnScrollAtTop: true,
      imageHiddenByScroll: false,
      detailsToggledManually: false,
      isMdUp: true,
      // iOS Safari fires scroll events during layout shifts (e.g. when modifiers
      // load). Ignore collapse until the user actually touches the panel.
      userHasScrolledModifierPanel: false,
      scrollCollapseGraceTimer: null
    };
  },
  created() {
    // Initialize item modal composable
    this._itemModalComposable = useItemModal(this);
  },
  mounted() {
    this.updateIsMdUp();
    window.addEventListener("resize", this.updateIsMdUp);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateIsMdUp);
    this.clearScrollCollapseGrace();
  },
  computed: {
    ...mapGetters(["getCurrencySymbol", "formatPrice", "baseUrl", "getStore"]),
    currency() {
      return this.getCurrencySymbol || "$";
    },
    /**
     * On desktop the image lives in its own pane and stays visible.
     * On mobile it collapses once the customization panel is scrolled down
     * (latched until the group changes to avoid a layout reflow loop).
     */
    showProductImage() {
      return this.isMdUp || !this.imageHiddenByScroll;
    },
    selectedModifiers() {
      return this._itemModalComposable.getSelectedModifiers();
    },
    isValid() {
      return this._itemModalComposable.isValid();
    },
    submitButtonLabel() {
      if (!this.isValid) return "Selections Required";
      return this.editingCartId ? "Update Item" : "Add to Order";
    },
    hasModifierGroups() {
      return (
        Array.isArray(this.modifierGroups) && this.modifierGroups.length > 0
      );
    },
    isSingleColumnProduct() {
      return !this.loadingModifiers && !this.hasModifierGroups;
    },
    currentGroup() {
      return this.modifierGroups[this.currentGroupIndex] || null;
    },
    isLastGroup() {
      return this.currentGroupIndex >= this.modifierGroups.length - 1;
    },
    /** Per-group status used by nav, tabs, and progress bar */
    groupMeta() {
      return this.modifierGroups.map((group, index) => {
        const count = this.getSelectedCountForGroup(group.id);
        const min = group.min_selection || 0;
        const required = min > 0;
        const satisfied = required ? count >= min : true;
        const interacted = count > 0;

        let status;
        let navBadge;
        let tabBadge;
        if (required) {
          if (satisfied) {
            status = "done";
            navBadge = "✓";
            tabBadge = "✓";
          } else {
            status = "required";
            navBadge = "Req";
            tabBadge = "Required";
          }
        } else if (interacted) {
          status = "done";
          navBadge = "✓";
          tabBadge = `✓ ${count}`;
        } else {
          status = "optional";
          navBadge = "Opt";
          tabBadge = "Optional";
        }

        return {
          id: group.id,
          name: group.name,
          index,
          count,
          required,
          satisfied,
          interacted,
          status,
          navBadge,
          tabBadge
        };
      });
    },
    requiredRemaining() {
      return this.groupMeta.filter((m) => m.required && !m.satisfied).length;
    },
    hasRequiredModifiers() {
      return this.groupMeta.some((m) => m.required);
    },
    requiredStatusText() {
      if (this.requiredRemaining === 0) {
        return "All required selections complete";
      }
      return `${this.requiredRemaining} required selection${
        this.requiredRemaining > 1 ? "s" : ""
      } remaining`;
    },
    /** Flattened list of currently selected modifiers for the summary card */
    selectionSummary() {
      return this.selectedModifiers.map((m) => ({
        name: m.name,
        price: m.price
      }));
    },
    /** Drives the footer call-to-action button */
    ctaState() {
      const allRequiredDone = this.isValid;
      const isLast = this.isLastGroup;

      if (allRequiredDone && isLast) {
        return {
          variant: "add",
          label: this.editingCartId ? "Update Item" : "Add to Order",
          hint: `${this.quantity} item${this.quantity > 1 ? "s" : ""}`,
          right: this.formatPrice(this.getTotalPrice()),
          icon: null,
          action: "submit",
          disabled: false
        };
      }

      if (!isLast) {
        const next = this.modifierGroups[this.currentGroupIndex + 1];
        const nextRequired = (next.min_selection || 0) > 0;
        let hint;
        if (nextRequired) {
          hint = "Required";
        } else if (allRequiredDone) {
          hint = "Optional";
        } else {
          hint = `${this.requiredRemaining} required left`;
        }
        return {
          variant: "next",
          label: `Next: ${next.name}`,
          hint,
          right: null,
          icon: "ArrowRight",
          action: "next",
          disabled: false
        };
      }

      // Last group but required selections are still missing
      const missing = this.groupMeta.find((m) => m.required && !m.satisfied);
      if (missing && missing.index === this.currentGroupIndex) {
        return {
          variant: "warn",
          label: "Select an option",
          hint: "Required selection needed",
          right: null,
          icon: null,
          action: "none",
          disabled: true
        };
      }
      return {
        variant: "warn",
        label: missing ? `Go to ${missing.name}` : "Selections required",
        hint: "Required selection needed",
        right: null,
        icon: "ArrowLeft",
        action: "goto",
        missingIndex: missing ? missing.index : 0,
        disabled: false
      };
    },
    ctaColorClass() {
      const map = {
        next: "bg-primary-600 hover:bg-primary-700",
        add: "bg-green-600 hover:bg-green-700",
        warn: "bg-red-500 hover:bg-red-600"
      };
      return map[this.ctaState.variant] || map.next;
    },
    modalContainerClass() {
      return this.isSingleColumnProduct
        ? "flex flex-col"
        : "flex flex-col md:flex-row";
    },
    modalContentClass() {
      return this.isSingleColumnProduct
        ? "p-0 flex flex-col w-full h-full"
        : "p-0 flex flex-col md:flex-row w-full h-full";
    }
  },
  watch: {
    item: {
      immediate: true,
      async handler() {
        if (this.item) {
          this.rightColumnScrollAtTop = true;
          this.imageHiddenByScroll = false;
          this.detailsToggledManually = false;
          this.userHasScrolledModifierPanel = false;
          this.startScrollCollapseGrace();
          this.currentGroupIndex = 0;
          this.hasAttemptedSubmit = false;
          this.isConfirmationShown = this.item.requiresConfirmation === true;
          this.quantity = this.item.quantity || 1;
          this.note = this.item.notes || "";
          const productId = this.item.id;
          this.currentFetchProductId = productId;

          if (productId) {
            await this.fetchModifierGroups(productId);
          } else {
            this.modifierGroups = [];
          }
        } else {
          this.isConfirmationShown = false;
          this.modifierGroups = [];
          this.singleSelections = {};
          this.currentGroupIndex = 0;
          this.currentFetchProductId = null;
          this.loadingModifiers = false;
        }
      }
    }
  },
  methods: {
    async fetchModifierGroups(expectedProductId) {
      const productId = expectedProductId || this.item?.id;
      if (!productId) {
        this.modifierGroups = [];
        return;
      }
      if (this.currentFetchProductId !== productId) return;

      this.loadingModifiers = true;
      try {
        const groups = await getProductModifierGroups(
          productId,
          this.getStore?.id
        );
        if (this.currentFetchProductId !== productId) return;
        const syntheticItem = { ...this.item, modifier_groups: groups };
        this._itemModalComposable.parseProduct(syntheticItem);
        this.currentGroupIndex = 0;
        if (this.currentFetchProductId === productId) {
          this.$nextTick(() => {
            this.resetModifierPanelScroll();
            this.$forceUpdate();
          });
        }
      } catch (error) {
        console.error("[ItemModal] Error fetching modifier groups:", error);
        if (this.currentFetchProductId === productId) {
          this.modifierGroups = [];
        }
      } finally {
        if (this.currentFetchProductId === productId) {
          this.loadingModifiers = false;
        }
      }
    },
    getSelectedCountForGroup(groupId) {
      return this._itemModalComposable.getSelectedCountForGroup(groupId);
    },
    handleModifierSelected({ group, modifier }) {
      // Delegate all logic to composable (including singleSelections update)
      // The composable will handle both singleSelections and selected_modifiers
      this._itemModalComposable.handleModifierSelected({ group, modifier });

      // Force update to ensure UI reflects changes
      this.$forceUpdate();
    },
    gotoGroup(index) {
      if (index < 0 || index >= this.modifierGroups.length) return;
      this.currentGroupIndex = index;
      // Preserve the user's current details visibility across group navigation.
      // Previously this reset imageHiddenByScroll/detailsToggledManually, which
      // re-opened details the user had intentionally hidden.
      this.$nextTick(() => {
        if (this.$refs.rightColumnScroll) {
          this.$refs.rightColumnScroll.scrollTop = 0;
        }
        this.rightColumnScrollAtTop = true;
      });
    },
    handleCtaClick() {
      const state = this.ctaState;
      if (state.action === "submit") {
        this.handleSubmit();
        return;
      }
      if (state.action === "next") {
        this.gotoGroup(this.currentGroupIndex + 1);
        return;
      }
      if (state.action === "goto") {
        this.hasAttemptedSubmit = true;
        this.gotoGroup(state.missingIndex);
        return;
      }
      // "none" – required selection still needed on the current group
      this.hasAttemptedSubmit = true;
    },
    badgeClass(status) {
      const map = {
        required: "bg-red-50 text-red-600",
        done: "bg-green-50 text-green-600",
        optional: "bg-gray-100 text-gray-500"
      };
      return map[status] || map.optional;
    },
    stepBarClass(meta, index) {
      if (index === this.currentGroupIndex) return "bg-primary-600";
      if (meta.status === "done") return "bg-green-500";
      return "bg-gray-200";
    },
    updateIsMdUp() {
      this.isMdUp =
        typeof window !== "undefined" ? window.innerWidth >= 768 : true;
    },
    clearScrollCollapseGrace() {
      if (this.scrollCollapseGraceTimer) {
        clearTimeout(this.scrollCollapseGraceTimer);
        this.scrollCollapseGraceTimer = null;
      }
    },
    startScrollCollapseGrace() {
      this.clearScrollCollapseGrace();
      this.userHasScrolledModifierPanel = false;
      // Ignore scroll-driven collapse briefly after open / content load.
      this.scrollCollapseGraceTimer = setTimeout(() => {
        this.scrollCollapseGraceTimer = null;
      }, 900);
    },
    resetModifierPanelScroll() {
      const el = this.$refs.rightColumnScroll;
      if (!el) return;
      el.scrollTop = 0;
      this.rightColumnScrollAtTop = true;
      this.imageHiddenByScroll = false;
      this.startScrollCollapseGrace();
    },
    onModifierPanelTouchStart() {
      this.userHasScrolledModifierPanel = true;
    },
    toggleDetails() {
      // Manual override of the scroll-driven collapse. If the user expands the
      // details while scrolled down, snap the panel back to the top so the
      // scroll handler doesn't immediately re-collapse them.
      const willExpand = this.imageHiddenByScroll;
      this.imageHiddenByScroll = !this.imageHiddenByScroll;
      // Once the user uses the button, hand full control to them and stop the
      // scroll-driven auto-collapse (it fights the manual state and flickers).
      this.detailsToggledManually = true;
      if (willExpand) {
        const el = this.$refs.rightColumnScroll;
        if (el && el.scrollTop > 8) {
          el.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    },
    onRightColumnScroll() {
      const el = this.$refs.rightColumnScroll;
      if (!el) {
        this.rightColumnScrollAtTop = true;
        return;
      }
      const top = el.scrollTop;
      this.rightColumnScrollAtTop = top <= 8;

      if (this.detailsToggledManually || this.scrollCollapseGraceTimer) {
        return;
      }

      // iOS Safari reports non-zero scrollTop during layout reflow (e.g. when
      // modifier options replace the loading state). Snap back unless the user
      // has actually touched the scroll panel.
      if (!this.userHasScrolledModifierPanel && top > 0 && top < 160) {
        el.scrollTop = 0;
        this.rightColumnScrollAtTop = true;
        return;
      }

      // One-way auto-collapse: hide the image/description once scrolled down.
      // Expanding is intentionally NOT done on scroll — collapsing shrinks the
      // left pane, which clamps scrollTop back toward the top and would re-trigger
      // an expand mid-swipe (flicker). Use the Show/Hide button to expand instead.
      if (top > 40 && this.userHasScrolledModifierPanel) {
        this.imageHiddenByScroll = true;
      }
    },
    getTotalPrice() {
      return this._itemModalComposable.getTotalPrice(this.item, this.quantity);
    },
    handleConfirm() {
      this.isConfirmationShown = false;
    },
    handleSubmit() {
      this.hasAttemptedSubmit = true;

      if (!this.isValid) return;

      const payload = {
        item: this.item,
        quantity: this.quantity,
        notes: this.note,
        modifiers: this._itemModalComposable.prepareModifiers(
          this.selectedModifiers
        )
      };

      if (this.editingCartId && this.onUpdateCartItem) {
        this.onUpdateCartItem(this.editingCartId, payload);
      } else {
        this.onAddToCart(payload);
      }
      this.onClose();
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fadeIn-0-2s {
  animation: fadeIn 0.2s ease-out;
}
</style>
